
// Combined Analytics Service (Google Analytics & Meta Pixel)
type EventParams = Record<string, string | number | boolean | string[] | undefined>;

// Track page views
export const trackPageView = (path: string): void => {
  // Google Analytics page tracking
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-6Z3JM8D9F3', {
      page_path: path,
    });
    console.log(`Google Analytics page view tracked: ${path}`);
  }
  
  // Meta Pixel page tracking
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'PageView');
    console.log(`Meta Pixel page view tracked: ${path}`);
  }
};

// Track custom events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  params?: EventParams
): void => {
  // Google Analytics event tracking
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...params,
    });
    console.log(`Google Analytics event tracked: ${action} in category ${category}`);
  }
  
  // Meta Pixel event tracking
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', action, {
      category: category,
      label: label,
      value: value,
      ...params,
    });
    console.log(`Meta Pixel event tracked: ${action} in category ${category}`);
  }
};

// Meta Pixel specific standard events
// Optional `eventId` is passed as the dedup key for Pixel + Conversion API matching.
export const trackMetaEvent = (
  standardEvent: string,
  params?: EventParams,
  eventId?: string,
): void => {
  if (typeof window !== 'undefined' && window.fbq) {
    if (eventId) {
      window.fbq('track', standardEvent, params, { eventID: eventId });
    } else {
      window.fbq('track', standardEvent, params);
    }
    console.log(
      `Meta Pixel standard event tracked: ${standardEvent}${eventId ? ` (eventID: ${eventId})` : ''}`,
    );
  }
};

// Track conversion events (Google Analytics only — Meta Purchase is sent via trackMetaEvent).
// Default currency is GBP (Nimi charges in GBP). `eventId` is used as GA transaction_id.
export const trackConversion = (
  value?: number,
  currency?: string,
  params?: EventParams,
  eventId?: string,
): void => {
  const resolvedCurrency = currency || 'GBP';

  // Google Analytics conversion tracking
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      value: value,
      currency: resolvedCurrency,
      transaction_id: eventId,
      ...params,
    });
  }
};

// Meta Pixel custom events (fbq trackCustom). Optional eventId for dedup.
export const trackMetaCustom = (
  name: string,
  params?: EventParams,
  eventId?: string,
): void => {
  if (typeof window !== 'undefined' && window.fbq) {
    if (eventId) {
      window.fbq('trackCustom', name, params, { eventID: eventId });
    } else {
      window.fbq('trackCustom', name, params);
    }
  }
};

// Run fn once per key across reloads (localStorage guard).
export const runOnce = (key: string, fn: () => void): void => {
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    // storage unavailable — still fire
  }
  fn();
};

// Standard Meta content params for an event ticket.
export const metaEventParams = (event: { id: string; title?: string; price?: number | null; currency?: string | null }) => ({
  content_ids: [event.id],
  content_name: event.title ?? '',
  content_type: 'product',
  content_category: 'event',
  value: Number(event.price ?? 0),
  currency: (event.currency || 'GBP').toUpperCase(),
});
